/**
 * One-shot recovery when a tab outlives a Vite deploy and lazy chunks 404.
 * After deploy, hashed `/assets/*.js` URLs change; a stale tab still requests
 * the old hash. A full reload fetches fresh HTML with current chunk names.
 */

const SESSION_FLAG_KEY = 'roam:vite-stale-chunk-reload';

const STALE_CHUNK_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /unable to preload css/i,
];

export function isStaleChunkLoadError(error: unknown): boolean {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : String(error);

  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
}

export function hasStaleChunkReloadBeenAttempted(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Sets the session flag and reloads once. Returns true if a reload was started.
 */
export function attemptStaleChunkReload(): boolean {
  if (typeof window === 'undefined') return false;
  if (hasStaleChunkReloadBeenAttempted()) return false;
  try {
    sessionStorage.setItem(SESSION_FLAG_KEY, '1');
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * Call once at app bootstrap (before React mount).
 */
export function installStaleChunkRecovery(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault();
    attemptStaleChunkReload();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (!isStaleChunkLoadError(event.reason)) return;
    if (attemptStaleChunkReload()) {
      event.preventDefault();
    }
  });
}
